import { AxiosInstance } from 'axios';
import baseAxios, { axiosWithoutCaseMiddleware } from '@/helpers/forms/axios';
import integrations from './integrations';
import senderDomains from './senderDomains';

export interface userNotificationInterface {
    id: string
    createdAt: string
    notificationType: 'action' | 'error' | 'success'
    body: string
    actionText: string
    actionUrl: string
    actionType: 'print_labels'
    actionData: object
    read: boolean
}

export const serviceProviderConfigurableNotifications = [
    'channelCredentialsUnauthorized',
    'billingCycleEndsTomorrow',
    'billOfLadingAddedToOutboundShipment',
    'cardDeclined',
    'cartonErrorItemDiscrepancyReport',
    'newInboundShipmentCreated',
    'newOutboundShipmentCreated',
    'channelCredentialsUpdated',
    'dailyManagerReport',
    'newTeamCreated',
    'merchantSupportRequest',
] as const;
export type ServiceProviderConfigurableNotificationsEnum = typeof serviceProviderConfigurableNotifications[number];

export const merchantConfigurableNotifications = [
    'billingFailed',
    'inboundShipmentReceivingReport',
    'invoiceReadyForPayment',
    'itemsRequireFixing',
    'outboundShipmentShipped',
] as const;
export type MerchantConfigurableNotificationsEnum = typeof merchantConfigurableNotifications[number];

export interface notificationSettingInterface {
    disabled: boolean
    emails: string[]
}

interface NotificationsApiReturn {
    integrations: ReturnType<typeof integrations>
    senderDomains: ReturnType<typeof senderDomains>
    fetchRecentUserNotifications: () => Promise<userNotificationInterface[]>
    updateUserNotificationsRead: () => Promise<void>
    fetchMerchantNotificationSettings: (merchantId: number) => Promise<Partial<Record<MerchantConfigurableNotificationsEnum, notificationSettingInterface>>>
    updateMerchantNotificationSettings: (merchantId: number, settings: Partial<Record<MerchantConfigurableNotificationsEnum, notificationSettingInterface>>) => Promise<void>
    fetchServiceProviderNotificationSettings: () => Promise<Partial<Record<ServiceProviderConfigurableNotificationsEnum, notificationSettingInterface>>>
    updateServiceProviderNotificationSettings: (settings: Partial<Record<ServiceProviderConfigurableNotificationsEnum, notificationSettingInterface>>) => Promise<void>
    fetchServiceProviderToMerchantNotificationSettings: () => Promise<Partial<Record<MerchantConfigurableNotificationsEnum, { disabled: boolean }>>>
    updateServiceProviderToMerchantNotificationSettings: (settings: Partial<Record<MerchantConfigurableNotificationsEnum, { disabled: boolean }>>) => Promise<void>
}

export default (
    axios: AxiosInstance = baseAxios,
    axiosWithoutCaseConversion: AxiosInstance = axiosWithoutCaseMiddleware,
): NotificationsApiReturn => ({
    integrations: integrations(axios),
    senderDomains: senderDomains(axiosWithoutCaseConversion),

    fetchRecentUserNotifications: async (): Promise<userNotificationInterface[]> => {
        const response = await axios.get<{ data: userNotificationInterface[] }>('/api/notifications/users/recent');

        return response.data.data;
    },

    updateUserNotificationsRead: async (): Promise<void> => {
        await axios.get('/api/notifications/users/read');
    },

    fetchMerchantNotificationSettings: async (merchantId: number): Promise<Partial<Record<MerchantConfigurableNotificationsEnum, notificationSettingInterface>>> => {
        const response = await axios.get<Partial<Record<MerchantConfigurableNotificationsEnum, notificationSettingInterface>>>(`/api/notifications/merchants/${merchantId}/settings`);

        return response.data;
    },

    updateMerchantNotificationSettings: async (merchantId: number, settings: Partial<Record<MerchantConfigurableNotificationsEnum, notificationSettingInterface>>): Promise<void> => {
        await axios.post(`/api/notifications/merchants/${merchantId}/settings`, settings);
    },

    fetchServiceProviderNotificationSettings: async (): Promise<Partial<Record<ServiceProviderConfigurableNotificationsEnum, notificationSettingInterface>>> => {
        const response = await axios.get<Partial<Record<ServiceProviderConfigurableNotificationsEnum, notificationSettingInterface>>>('/api/notifications/serviceProviders/settings');

        return response.data;
    },

    updateServiceProviderNotificationSettings: async (settings: Partial<Record<ServiceProviderConfigurableNotificationsEnum, notificationSettingInterface>>): Promise<void> => {
        await axios.post('/api/notifications/serviceProviders/settings', settings);
    },

    fetchServiceProviderToMerchantNotificationSettings: async (): Promise<Partial<Record<MerchantConfigurableNotificationsEnum, { disabled: boolean }>>> => {
        const response = await axios.get<Partial<Record<MerchantConfigurableNotificationsEnum, { disabled: boolean }>>>('/api/notifications/serviceProviders/toMerchantSettings');

        return response.data;
    },

    updateServiceProviderToMerchantNotificationSettings: async (settings: Partial<Record<MerchantConfigurableNotificationsEnum, { disabled: boolean }>>): Promise<void> => {
        await axios.post('/api/notifications/serviceProviders/toMerchantSettings', settings);
    },
});
